import { z } from 'zod';
import { isoDateSchema, moneyMinorSchema } from '../api';
import { COLLECTABLE_PAYMENT_MODE, PRODUCT_TYPE, STOCK_MOVEMENT_TYPE, STORE_TYPE } from '../enums';
import { BATCH_STATUS, MAX_DOCUMENT_TOTAL_MINOR, MAX_LINE_TOTAL_MINOR, STOCK_LOSS_CATEGORY } from '../procurement';
import { supervisorOverrideSchema } from './procurement';
import { TAX_CATEGORIES } from '../countries/types';
export const upsertProductSchema = z.object({
    type: z.enum(PRODUCT_TYPE).default('drug'),
    code: z.string().trim().toUpperCase().min(2).max(32),
    genericName: z.string().trim().min(2).max(200),
    brandName: z.string().trim().max(200).optional(),
    form: z.string().trim().max(60).optional(),
    strength: z.string().trim().max(80).optional(),
    packSize: z.number().int().min(1).max(100000).default(1),
    unit: z.string().trim().max(24).default('unit'),
    manufacturer: z.string().trim().max(160).optional(),
    barcodes: z.array(z.string().trim().max(64)).max(10).default([]),
    isControlled: z.boolean().default(false),
    isRxRequired: z.boolean().default(false),
    storageConditions: z.string().trim().max(200).optional(),
    /** Included in the tenant formulary — hides non-stocked items from search. */
    inFormulary: z.boolean().default(true),
    therapeuticClass: z.string().trim().max(120).optional(),
    /** Alternative products offered when a substitution is proposed. */
    alternativeProductIds: z.array(z.string().uuid()).max(20).default([]),
    taxPct: z.number().min(0).max(100).default(0),
    taxCategory: z.enum(TAX_CATEGORIES).nullable().optional(),
    reorderLevel: z.number().min(0).max(1000000).default(0),
    maxLevel: z.number().min(0).max(1000000).default(0),
    isActive: z.boolean().default(true),
});
export const upsertStoreSchema = z.object({
    branchId: z.string().uuid(),
    code: z.string().trim().toUpperCase().min(2).max(16),
    name: z.string().trim().min(2).max(160),
    type: z.enum(STORE_TYPE).default('main_pharmacy'),
    departmentId: z.string().uuid().nullable().optional(),
    /** Ward satellite stores are tied to a ward for the IPD indent flow. */
    wardId: z.string().uuid().nullable().optional(),
    isSellingPoint: z.boolean().default(true),
    isActive: z.boolean().default(true),
});
export const posLineSchema = z.object({
    productId: z.string().uuid(),
    /** Omit to let FEFO pick batches automatically. */
    stockBatchId: z.string().uuid().nullable().optional(),
    qty: z.number().min(0.001).max(100000),
    discountPct: z.number().min(0).max(100).default(0),
    prescriptionItemId: z.string().uuid().nullable().optional(),
    /** Recorded when the pharmacist substitutes a different brand (SRS 7.2). */
    substitutedForProductId: z.string().uuid().nullable().optional(),
    substitutionReason: z.string().trim().max(240).optional(),
});
export const posSaleSchema = z.object({
    storeId: z.string().uuid(),
    patientId: z.string().uuid().nullable().optional(),
    prescriptionId: z.string().uuid().nullable().optional(),
    externalRx: z
        .object({
        prescriberName: z.string().trim().min(2).max(160),
        registrationNo: z.string().trim().min(3).max(60),
        issuedOn: z.string().date(),
        reference: z.string().trim().max(80).optional(),
    })
        .optional(),
    walkInName: z.string().trim().max(160).optional(),
    walkInPhone: z.string().trim().max(20).optional(),
    payerId: z.string().uuid().nullable().optional(),
    lines: z.array(posLineSchema).min(1).max(100),
    payments: z
        .array(z.object({
        mode: z.enum(COLLECTABLE_PAYMENT_MODE),
        amountMinor: moneyMinorSchema.min(1),
        txnRef: z.string().trim().max(120).optional(),
    }))
        .default([]),
    expectedDueMinor: moneyMinorSchema.min(0).optional(),
    discountReason: z.string().trim().max(300).optional(),
    notes: z.string().max(600).optional(),
});
export const posQuoteSchema = z.object({
    storeId: z.string().uuid(),
    patientId: z.string().uuid(),
    prescriptionId: z.string().uuid().nullable().optional(),
    lines: z.array(posLineSchema).min(1).max(100),
});
export const posPackageAllowanceQuerySchema = z.object({
    storeId: z.string().uuid(),
    patientId: z.string().uuid(),
});
export const holdSaleSchema = z.object({
    storeId: z.string().uuid(),
    label: z.string().trim().min(1).max(60),
    payload: z.record(z.string(), z.unknown()),
});
export const salesReturnSchema = z.object({
    invoiceId: z.string().uuid(),
    lines: z
        .array(z.object({
        invoiceItemId: z.string().uuid(),
        qty: z.number().min(0.001).max(100_000),
    }))
        .min(1),
    reason: z.enum(['wrong_item', 'damaged', 'expired', 'patient_refused', 'other']),
    notes: z.string().max(600).optional(),
    supervisorOverride: supervisorOverrideSchema.optional(),
});
export const stockAdjustmentSchema = z.object({
    storeId: z.string().uuid(),
    lines: z
        .array(z.object({
        stockBatchId: z.string().uuid(),
        qtyDelta: z.number().min(-1_000_000).max(1_000_000),
        reason: z.string().trim().min(3).max(240),
        lossCategory: z.enum(STOCK_LOSS_CATEGORY).optional(),
    }))
        .min(1)
        .max(200),
    type: z.enum(STOCK_MOVEMENT_TYPE).default('adjustment'),
});
export const stockTransferSchema = z.object({
    fromStoreId: z.string().uuid(),
    toStoreId: z.string().uuid(),
    lines: z
        .array(z.object({ stockBatchId: z.string().uuid(), qty: z.number().min(0.001) }))
        .min(1)
        .max(200),
    notes: z.string().max(600).optional(),
});
export const upsertSupplierSchema = z.object({
    code: z.string().trim().toUpperCase().min(2).max(16),
    name: z.string().trim().min(2).max(200),
    contactPerson: z.string().trim().max(160).optional(),
    phone: z.string().trim().max(20).optional(),
    email: z.string().email().optional(),
    address: z.string().trim().max(400).optional(),
    taxNumber: z.string().trim().max(60).optional(),
    paymentTermsDays: z.number().int().min(0).max(365).default(30),
    isActive: z.boolean().default(true),
});
function capDocumentTotals(lines, context, lineField, documentName) {
    let total = 0;
    lines.forEach((line, index) => {
        if (line.grossMinor > MAX_LINE_TOTAL_MINOR || line.netMinor > MAX_LINE_TOTAL_MINOR) {
            context.addIssue({ code: 'custom', path: ['lines', index, lineField], message: 'The total of this line is larger than the system accepts' });
        }
        total += line.netMinor;
    });
    if (total > MAX_DOCUMENT_TOTAL_MINOR) {
        context.addIssue({ code: 'custom', path: ['lines'], message: `The total of this ${documentName} is larger than the system accepts` });
    }
}
function receivedLineAmounts(line) {
    const grossMinor = Math.round(line.qty * line.purchasePriceMinor);
    const taxableMinor = grossMinor - Math.round((grossMinor * line.discountPct) / 100);
    return { grossMinor, netMinor: taxableMinor + Math.round((taxableMinor * line.taxPct) / 100) };
}
export const createPurchaseOrderSchema = z
    .object({
    supplierId: z.string().uuid(),
    storeId: z.string().uuid(),
    expectedAt: z.string().datetime().nullable().optional(),
    lines: z
        .array(z.object({
        productId: z.string().uuid(),
        qty: z.number().min(0.001).max(100_000),
        unitCostMinor: moneyMinorSchema.min(0),
    }))
        .min(1)
        .max(300),
    notes: z.string().max(600).optional(),
})
    .superRefine((value, context) => {
    capDocumentTotals(value.lines.map((line) => {
        const totalMinor = Math.round(line.qty * line.unitCostMinor);
        return { grossMinor: totalMinor, netMinor: totalMinor };
    }), context, 'unitCostMinor', 'purchase order');
});
export const createGrnSchema = z
    .object({
    supplierId: z.string().uuid(),
    storeId: z.string().uuid(),
    purchaseOrderId: z.string().uuid().nullable().optional(),
    supplierInvoiceNo: z.string().trim().max(80).optional(),
    receivedAt: z.string().datetime().optional(),
    lines: z
        .array(z.object({
        productId: z.string().uuid(),
        batchNo: z.string().trim().min(1).max(64),
        expiryDate: isoDateSchema,
        qty: z.number().min(0.001).max(100_000),
        freeQty: z.number().min(0).max(100_000).default(0),
        purchasePriceMinor: moneyMinorSchema.min(0),
        salePriceMinor: moneyMinorSchema.min(0),
        taxPct: z.number().min(0).max(100).default(0),
        discountPct: z.number().min(0).max(100).default(0),
    }))
        .min(1)
        .max(300),
    notes: z.string().max(600).optional(),
})
    .superRefine((value, context) => {
    capDocumentTotals(value.lines.map(receivedLineAmounts), context, 'purchasePriceMinor', 'goods received note');
});
export const createIndentSchema = z.object({
    fromStoreId: z.string().uuid(),
    wardId: z.string().uuid().nullable().optional(),
    admissionId: z.string().uuid().nullable().optional(),
    lines: z
        .array(z.object({
        productId: z.string().uuid(),
        qty: z.number().min(0.001).max(100_000),
        medicationOrderId: z.string().uuid().nullable().optional(),
        notes: z.string().max(300).optional(),
    }))
        .min(1)
        .max(200),
    notes: z.string().max(600).optional(),
});
export const indentFromCpoeSchema = z.object({
    admissionId: z.string().uuid(),
    fromStoreId: z.string().uuid(),
});
export const rejectIndentSchema = z.object({
    reason: z.string().trim().min(1).max(400),
});
export const issueIndentSchema = z.object({
    lines: z
        .array(z.object({
        indentItemId: z.string().uuid(),
        stockBatchId: z.string().uuid().nullable().optional(),
        qty: z.number().min(0),
    }))
        .min(1),
    /** Ward-stock model issues are not billed to a patient; unit-dose issues are. */
    chargeToAdmission: z.boolean().default(true),
    notes: z.string().max(600).optional(),
});
export const stockQuerySchema = z.object({
    storeId: z.string().uuid().optional(),
    productId: z.string().uuid().optional(),
    q: z.string().trim().max(120).optional(),
    status: z.enum(BATCH_STATUS).optional(),
    expiringInDays: z.coerce.number().int().min(1).max(365).optional(),
    belowReorder: z.preprocess((v) => (typeof v === 'string' ? ['true', '1', 'yes'].includes(v.toLowerCase()) : v), z.boolean().optional()),
    includeZero: z.preprocess((v) => (typeof v === 'string' ? ['true', '1', 'yes'].includes(v.toLowerCase()) : v), z.boolean().default(false)),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().optional(),
});
//# sourceMappingURL=pharmacy.js.map